import { Alert } from '@risk-smart/themed-cloudscape-components';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type {
  ObjectToolbarConfig,
  ToolbarActionConfig,
} from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import type { ActionItem } from 'src/components/actions-button/ActionsButton';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteAssessment } from '@/hooks/mutations/assessment/useDeleteAssessment';
import { useGetAssessmentById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import {
  addAssessmentActivityUrl,
  addAssessmentRCSAActivityUrl,
} from '@/utils/urls';

import { useActivitiesStore } from './tabs/activities/store/useActivitiesStore';
import { DeleteTypeEnum } from './tabs/activities/types';
import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'activities'
    | 'details'
    | 'findings'
    | 'impacts'
    | 'linkedItems';
  activityMode?:
    | 'addActivity'
    | 'addRCSA'
    | 'list'
    | 'updateActivity'
    | 'updateRCSA'
    | undefined;
};

const Page: FC<Props> = ({ activeTabId, activityMode = undefined }) => {
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const assessmentId = useGetGuidParam('assessmentId');
  const {
    setIsActivityDeleteModalVisible,
    setDeleteType,
    selectedActivities,
    selectedRCSAActivities,
  } = useActivitiesStore();
  const [exportItem, { loading: exporting }] = useExporter(assessmentId);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const navigate = useNavigate();
  const parentUrl = useGetDetailParentPath(assessmentId);
  const detailsPath = useGetDetailPath(assessmentId);
  const { deleteAssessment, loading: deleteLoading } = useDeleteAssessment();

  const wizardFeatureEnabled = useIsModuleEnabled(
    'risk.subModules.rcsa_wizard'
  );

  const { t: st } = useTranslation(['common'], { keyPrefix: 'assessments' });
  const { data, loading, error } = useGetAssessmentById({
    queryArgs: { Id: assessmentId },
  });
  if (error) {
    throw error;
  }

  const assessment = data?.assessment[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Assessment,
    parent: assessment,
    assessmentActivityMode: activityMode,
    assessmentMode: 'rating',
    hrefRoot: detailsPath,
  });
  const onDelete = useDeleteResultNotification({
    entityName: st('entity'),
    asyncAction: async () => {
      if (!assessment) {
        return false;
      }
      await deleteAssessment(assessment.Id);
      await navigate(parentUrl);

      return true;
    },
  });

  const {
    hasPermission: canDeleteAssessment,
    loading: canDeleteAssessmentLoading,
  } = useHasPermissionQuery(`delete:assessment`, assessment);
  const {
    hasPermission: canDeleteAssessmentActivities,
    loading: canDeleteAssessmentActivitiesLoading,
  } = useHasPermissionQuery(`delete:assessment_activity`, assessment);
  const {
    hasPermission: canInsertAssessmentActivities,
    loading: canInsertAssessmentActivitiesLoading,
  } = useHasPermissionQuery(`insert:assessment_activity`, assessment);

  if (!loading && !assessment?.Id) {
    throw new PageNotFound(`Assessment with id ${assessmentId} not found`);
  }

  const counter =
    assessment &&
    `(${getFriendlyId(Parent_Type_Enum.Assessment, assessment.SequentialId)})`;
  const fallbackTitle = st('fallback_title');

  // Object-level toolbar (CUS-163) — flag-gated. Relocates the legacy header
  // ActionsButton into the toolbar: Export + Delete Assessment (danger), plus
  // the activity-tab contextual actions (Add Activity / Plan RCSA as primary,
  // Delete Activity / Delete RCSA as danger) that only appear when
  // `activityMode === 'list'`. Child-type activity actions are gated by the
  // page's pre-computed permission booleans (the resolver can't gate a child
  // type against the assessment parentObject), so they carry no `permission`.
  // Save disc / Cancel X are bridged from the active tab's form via
  // useObjectToolbarFormStore.
  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const activityListMode = activityMode === 'list';
    const canAddActivity =
      activityListMode &&
      canInsertAssessmentActivities &&
      !canInsertAssessmentActivitiesLoading;
    const canDeleteActivities =
      activityListMode &&
      canDeleteAssessmentActivities &&
      !canDeleteAssessmentActivitiesLoading;

    const primary: ToolbarActionConfig[] = [
      ...(canAddActivity
        ? [
            {
              id: 'add-activity',
              label: t('assessmentActivities.add_button'),
              iconName: 'plus',
              onClick: () => navigate(addAssessmentActivityUrl(assessmentId)),
            } satisfies ToolbarActionConfig,
          ]
        : []),
      ...(activityListMode &&
      wizardFeatureEnabled &&
      canInsertAssessmentActivities
        ? [
            {
              id: 'plan-rcsa',
              // No i18n key exists for this label; the legacy header hardcodes
              // the same string, so mirror it for flag on/off parity.
              label: 'Plan RCSA',
              iconName: 'clipboard-check',
              onClick: () =>
                navigate(addAssessmentRCSAActivityUrl(assessmentId)),
            } satisfies ToolbarActionConfig,
          ]
        : []),
    ];

    const danger: ToolbarActionConfig[] = [
      ...(canDeleteActivities
        ? [
            {
              id: 'delete-activity',
              label: t('assessmentActivities.delete_activity_button'),
              iconName: 'trash-01',
              disabled: selectedActivities.length === 0,
              onClick: () => {
                setDeleteType(DeleteTypeEnum.Activity);
                setIsActivityDeleteModalVisible(true);
              },
            } satisfies ToolbarActionConfig,
          ]
        : []),
      ...(canDeleteActivities && wizardFeatureEnabled
        ? [
            {
              id: 'delete-rcsa',
              label: t('assessmentActivities.delete_rcsa_button'),
              iconName: 'trash-01',
              disabled: selectedRCSAActivities.length === 0,
              onClick: () => {
                setDeleteType(DeleteTypeEnum.RCSA);
                setIsActivityDeleteModalVisible(true);
              },
            } satisfies ToolbarActionConfig,
          ]
        : []),
      ...(canDeleteAssessment && !canDeleteAssessmentLoading
        ? [
            {
              id: 'delete',
              label: st('delete_button'),
              iconName: 'trash-01',
              permission: 'delete:assessment',
              onClick: () => setIsDeleteModalVisible(true),
            } satisfies ToolbarActionConfig,
          ]
        : []),
    ];

    return {
      header: {
        // `||` (not `??`) so an empty-string Title falls back to fallbackTitle
        // for flag on/off parity.
        title: assessment?.Title || fallbackTitle,
        // Pass the already-parenthesised counter (rendered verbatim).
        counter: counter || undefined,
      },
      parentObject: assessment,
      save: true,
      actions: {
        ...(primary.length > 0 ? { primary } : {}),
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        ...(danger.length > 0 ? { danger } : {}),
      },
    };
  }, [
    activityMode,
    assessment,
    assessmentId,
    counter,
    fallbackTitle,
    formConfig,
    exportItem,
    exporting,
    navigate,
    wizardFeatureEnabled,
    canInsertAssessmentActivities,
    canInsertAssessmentActivitiesLoading,
    canDeleteAssessmentActivities,
    canDeleteAssessmentActivitiesLoading,
    canDeleteAssessment,
    canDeleteAssessmentLoading,
    selectedActivities,
    selectedRCSAActivities,
    setDeleteType,
    setIsActivityDeleteModalVisible,
    t,
    st,
  ]);

  const buttons: ActionItem[] = [];

  if (activityMode === 'list') {
    if (
      canInsertAssessmentActivities &&
      !canInsertAssessmentActivitiesLoading
    ) {
      buttons.push({
        text: 'Add Activity',
        id: 'addActivityButton',
        onItemClick: () => {
          navigate(addAssessmentActivityUrl(assessmentId));
        },
      });
    }
    if (
      canDeleteAssessmentActivities &&
      !canDeleteAssessmentActivitiesLoading
    ) {
      buttons.push({
        text: 'Delete Activity',
        id: 'deleteActivityButton',
        onItemClick: () => {
          setDeleteType(DeleteTypeEnum.Activity);
          setIsActivityDeleteModalVisible(true);
        },
        disabled: selectedActivities.length === 0,
      });
    }
    if (wizardFeatureEnabled) {
      if (canInsertAssessmentActivities) {
        buttons.push({
          text: 'Plan RCSA',
          id: 'planRCSAButton',
          onItemClick: () => {
            navigate(addAssessmentRCSAActivityUrl(assessmentId));
          },
        });
      }
      if (
        canDeleteAssessmentActivities &&
        !canDeleteAssessmentActivitiesLoading
      ) {
        buttons.push({
          text: 'Delete RCSA',
          id: 'deleteRCSAButton',
          onItemClick: () => {
            setDeleteType(DeleteTypeEnum.RCSA);
            setIsActivityDeleteModalVisible(true);
          },
          disabled: selectedRCSAActivities.length === 0,
        });
      }
    }
  }

  buttons.push({
    text: t('export.export'),
    id: 'exportAssessmentButton',
    onItemClick: () => {
      exportItem();
    },
    disabled: exporting,
  });

  if (canDeleteAssessment && !canDeleteAssessmentLoading) {
    buttons.push({
      text: 'Delete Assessment',
      id: 'deleteAssessmentButton',
      // Wait for the entity to hydrate. `onDelete` guards on
      // `if (!assessment) return false`, which surfaces as a bare "Failed to
      // save changes" with NO request issued, so a click inside the hydration
      // window looks like a broken delete (ENG-616). Disabling also restores
      // Playwright's actionability wait.
      disabled: !assessment,
      onItemClick: () => {
        setIsDeleteModalVisible(true);
      },
    });
  }

  const actions =
    buttons.length > 1 ? (
      [
        <ActionsButton
          key={'actions'}
          // TODO: translattion
          buttonText={'Actions'}
          testId={'actionsMenu'}
          items={buttons}
        />,
      ]
    ) : (
      <Button
        variant={'normal'}
        formAction={'none'}
        onClick={buttons[0].onItemClick}
      >
        {buttons[0].text}
      </Button>
    );

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {actions}
        </SpaceBetween>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={assessment?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.Assessment}
        parent={assessment}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.Assessment}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={assessment}
      />

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        <SpaceBetween size={'s'}>
          <Alert type={'warning'} dismissible={false}>
            {st('rscaDeletionWaringMessage')}
          </Alert>
          {st('confirm_delete_message')}
        </SpaceBetween>
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
